import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import {
  useGetEventsQuery,
  useDeleteEventMutation,
  useUpdateEventVisibilityMutation,
} from "../features/events/eventsApiSlice";
import { useSelector } from "react-redux";
import { selectCurrentUserData } from "../features/auth/authSlice";
import useAuth from "../hooks/useAuth";
import EventFormStepper from "../components/events/stepper/EventFormStepper";
import EventParticipantsDialog from "../components/events/EventParticipantsDialog";
import EventDetailDialog from "../components/events/EventDetailDialog";
import EventFeeCollectionDialog from "../components/events/EventFeeCollectionDialog";
import { useDispatch } from "react-redux";
import { setDialog } from "../features/dialog/dialogSlice";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EventCardAdmin, toEventTypeLabel } from "../components/events/cards";

export default function EventsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userData = useSelector(selectCurrentUserData);
  const { isAdmin, isSuperAdmin, isUser, isEditor } = useAuth();
  const currentUserId = String(userData?.id || userData?._id || "");
  const openedFromQueryRef = useRef(false);
  const {
    data: allEvents = [],
    isLoading,
    isError,
    refetch,
  } = useGetEventsQuery();
  const [deleteEvent, { isLoading: isDeleting }] = useDeleteEventMutation();
  const [updateEventVisibility, { isLoading: isUpdatingVisibility }] =
    useUpdateEventVisibilityMutation();

  const events = useMemo(
    () => (Array.isArray(allEvents) ? allEvents : []),
    [allEvents]
  );

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");

  const filtered = useMemo(() => {
    let arr = events;
    if (statusFilter !== "ALL") {
      arr = arr.filter((e) => e.status === statusFilter);
    }
    if (typeFilter !== "ALL") {
      arr = arr.filter(
        (e) => String(e?.eventType || "").toUpperCase() === String(typeFilter).toUpperCase()
      );
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter(
        (e) =>
          (e.eventName || "").toLowerCase().includes(q) ||
          toEventTypeLabel(e.eventType).toLowerCase().includes(q) ||
          (e.locationName || "").toLowerCase().includes(q) ||
          (e.city || "").toLowerCase().includes(q) ||
          (e.tags || []).some((t) => t.toLowerCase().includes(q))
      );
    }
    return [...arr].sort(
      (a, b) =>
        new Date(b.eventDateBegin || 0) - new Date(a.eventDateBegin || 0)
    );
  }, [events, search, statusFilter, typeFilter]);

  // Form dialog
  const [openForm, setOpenForm] = useState(false);
  const [editEvent, setEditEvent] = useState(null);

  // Participants dialog
  const [openParticipants, setOpenParticipants] = useState(false);
  const [activeEvent, setActiveEvent] = useState(null);

  // Detail dialog
  const [openDetail, setOpenDetail] = useState(false);
  const [detailEvent, setDetailEvent] = useState(null);
  const [detailCanManage, setDetailCanManage] = useState(false);
  const [detailInitialTab, setDetailInitialTab] = useState(0);
  const [openFeeCollection, setOpenFeeCollection] = useState(false);
  const [feeEvent, setFeeEvent] = useState(null);

  const handleCreate = () => {
    setEditEvent(null);
    setOpenForm(true);
  };

  const handleEdit = (ev) => {
    setEditEvent(ev);
    setOpenForm(true);
  };

  const handleAfterSave = () => {
    setOpenForm(false);
    setEditEvent(null);
    refetch();
  };

  const handleOpenParticipants = (ev) => {
    setActiveEvent(ev);
    setOpenParticipants(true);
  };

  const handleParticipantsClosed = (changed) => {
    setOpenParticipants(false);
    setActiveEvent(null);
    if (changed) refetch();
  };

  const handleOpenDetail = (ev, canManage, initialTab = 0) => {
    setDetailEvent(ev);
    setDetailCanManage(!!canManage);
    setDetailInitialTab(initialTab);
    setOpenDetail(true);
  };

  const handleCloseDetail = () => {
    setOpenDetail(false);
    setDetailEvent(null);
    setDetailCanManage(false);
    setDetailInitialTab(0);
    refetch();
  };

  const handleOpenFeeCollection = (ev) => {
    setFeeEvent(ev);
    setOpenFeeCollection(true);
  };

  const handleOpenMemberBookingDetails = (ev) => {
    const eventId = String(ev?._id || ev?.id || "").trim();
    if (!eventId) return;
    navigate({
      pathname: "/dashboard/veranstaltungen",
      search: `?eventId=${encodeURIComponent(eventId)}`,
    });
  };

  const canOpenMemberBookingDetails =
    (isUser || isEditor || isAdmin) && Boolean(userData?.memberRef);

  const buildPublicBookingLink = (ev) => {
    const eventId = ev?._id || ev?.id;
    if (!eventId) return "";
    return `${window.location.origin}/public/event/${eventId}`;
  };

  const handleOpenPublicBooking = (ev) => {
    const url = buildPublicBookingLink(ev);
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleCopyPublicBookingLink = async (ev) => {
    const url = buildPublicBookingLink(ev);
    if (!url) return;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement("textarea");
        input.value = url;
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.focus();
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }

      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Link kopiert",
          data: { successText: "Der öffentliche Buchungslink wurde in die Zwischenablage kopiert." },
        })
      );
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Kopieren fehlgeschlagen",
          data: { errorText: "Der Link konnte nicht kopiert werden. Bitte kopieren Sie ihn manuell." },
        })
      );
    }
  };

  const handleShowPublicBookingQR = (ev) => {
    const url = buildPublicBookingLink(ev);
    if (!url) return;

    dispatch(
      setDialog({
        title: "QR Code für Buchungsseite",
        content: "QR",
        type: "SHOW",
        value: url,
      })
    );
  };

  const handleCloseFeeCollection = () => {
    setOpenFeeCollection(false);
    setFeeEvent(null);
    refetch();
  };

  useEffect(() => {
    const eventId = searchParams.get("eventId");
    if (!eventId || openedFromQueryRef.current || !Array.isArray(events)) {
      return;
    }

    const matchedEvent = events.find((event) => String(event?._id) === eventId);
    if (!matchedEvent) return;

    openedFromQueryRef.current = true;
    const canManage =
      isAdmin || isSuperAdmin || String(matchedEvent.creator) === currentUserId;
    handleOpenDetail(matchedEvent, canManage);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("eventId");
    setSearchParams(nextParams, { replace: true });
  }, [currentUserId, events, isAdmin, isSuperAdmin, searchParams, setSearchParams]);

  const handleDelete = async (ev) => {
    const ok = window.confirm(
      `Veranstaltung "${ev.eventName}" wirklich löschen?`
    );
    if (!ok) return;
    try {
      await deleteEvent(ev._id || ev.id).unwrap();
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Veranstaltung gelöscht",
          data: { successText: `${ev.eventName} wurde gelöscht.` },
        })
      );
      refetch();
    } catch (e) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler beim Löschen",
          data: { errorText: e?.data?.message || "Löschen fehlgeschlagen." },
        })
      );
    }
  };

  const handleToggleVisibility = async (ev) => {
    try {
      await updateEventVisibility({
        id: ev._id || ev.id,
        isPublic: !ev.isPublic,
      }).unwrap();
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: ev.isPublic ? "Veranstaltung verborgen" : "Veranstaltung veröffentlicht",
          data: {
            successText: ev.isPublic
              ? "Die Veranstaltung ist nicht mehr für Mitglieder sichtbar."
              : "Die Veranstaltung ist jetzt für Mitglieder sichtbar.",
          },
        })
      );
      refetch();
    } catch (e) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Sichtbarkeit konnte nicht aktualisiert werden",
          data: { errorText: e?.data?.message || "Bitte erneut versuchen." },
        })
      );
    }
  };

  return (
    <>
      <Helmet>
        <title> Dashboard: Veranstaltungen </title>
      </Helmet>

      <Container sx={{ pb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="h4">Veranstaltungen</Typography>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleCreate}
            startIcon={<Icon icon="mdi:plus" />}
          >
            Neue Veranstaltung
          </Button>
        </Stack>

        {/* Filters */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <TextField
            size="small"
            placeholder="Suche (Titel, Ort, Tags)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <Icon
                  icon="mdi:magnify"
                  width={20}
                  style={{ marginRight: 8, opacity: 0.5 }}
                />
              ),
            }}
          />
          <TextField
            size="small"
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="ALL">Alle</MenuItem>
            <MenuItem value="geplant">Geplant</MenuItem>
            <MenuItem value="abgesagt">Abgesagt</MenuItem>
            <MenuItem value="abgeschlossen">Abgeschlossen</MenuItem>
          </TextField>
          <TextField
            size="small"
            select
            label="Typ"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="ALL">Alle Typen</MenuItem>
            <MenuItem value="EVENT">Veranstaltung</MenuItem>
            <MenuItem value="TESTING">Prüfung</MenuItem>
            <MenuItem value="SEMINAR">Seminar</MenuItem>
          </TextField>
        </Stack>

        {/* Status messages */}
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          {isLoading && (
            <>
              <CircularProgress size={18} />
              <Typography variant="body2">Laden…</Typography>
            </>
          )}
          {isError && (
            <Typography color="error">
              Fehler beim Laden.{" "}
              <Button size="small" onClick={refetch}>
                Erneut laden
              </Button>
            </Typography>
          )}
          {isDeleting && (
            <Typography variant="body2">Lösche…</Typography>
          )}
          {isUpdatingVisibility && (
            <Typography variant="body2">Aktualisiere Sichtbarkeit…</Typography>
          )}
        </Box>

        {/* Event cards */}
        <Grid container spacing={2}>
          {filtered.map((ev) => {
            const canEdit =
              isAdmin || isSuperAdmin || String(ev.creator) === currentUserId;
            return (
              <Grid item xs={12} sm={6} md={4} key={ev._id || ev.id}>
                <EventCardAdmin
                  event={ev}
                  canEdit={canEdit}
                  onEdit={() => handleEdit(ev)}
                  onDelete={() => handleDelete(ev)}
                  onManageParticipants={() =>
                    ev?.registrationType === "booking"
                      ? handleOpenDetail(ev, canEdit, 1)
                      : handleOpenParticipants(ev)
                  }
                  onDetail={() => handleOpenDetail(ev, canEdit)}
                  onToggleVisibility={() => handleToggleVisibility(ev)}
                  onCollectFees={() => handleOpenFeeCollection(ev)}
                  onOpenPublicBooking={() => handleOpenPublicBooking(ev)}
                  onCopyPublicBookingLink={() => handleCopyPublicBookingLink(ev)}
                  onShowPublicBookingQR={() => handleShowPublicBookingQR(ev)}
                  canOpenMemberBookingDetails={
                    canOpenMemberBookingDetails && Boolean(ev?.isPublic)
                  }
                  onMemberBookingDetail={() => handleOpenMemberBookingDetails(ev)}
                />
              </Grid>
            );
          })}

          {!isLoading && filtered.length === 0 && (
            <Grid item xs={12}>
              <EmptyState onCreate={handleCreate} />
            </Grid>
          )}
        </Grid>
      </Container>

      {/* Create/Edit dialog */}
      <EventFormStepper
        open={openForm}
        onClose={() => setOpenForm(false)}
        onSaved={handleAfterSave}
        initialEvent={editEvent}
      />

      {/* Manage participants */}
      {!!activeEvent && (
        <EventParticipantsDialog
          open={openParticipants}
          onClose={handleParticipantsClosed}
          eventId={activeEvent._id || activeEvent.id}
          eventTitle={activeEvent.eventName}
        />
      )}

      {/* Detail with comments */}
      {!!detailEvent && (
        <EventDetailDialog
          open={openDetail}
          onClose={handleCloseDetail}
          eventId={detailEvent._id || detailEvent.id}
          canManage={detailCanManage}
          initialTab={detailInitialTab}
        />
      )}

      {!!feeEvent && (
        <EventFeeCollectionDialog
          open={openFeeCollection}
          onClose={handleCloseFeeCollection}
          event={feeEvent}
        />
      )}
    </>
  );
}

// ---- Sub-components ----

const EmptyState = ({ onCreate }) => (
  <Card variant="outlined" sx={{ p: 3, textAlign: "center" }}>
    <Typography variant="h6" sx={{ mb: 1 }}>
      Noch keine Veranstaltungen
    </Typography>
    <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
      Legen Sie eine neue Veranstaltung an, um Teilnehmer zu verwalten.
    </Typography>
    <Button
      variant="contained"
      color="secondary"
      onClick={onCreate}
      startIcon={<Icon icon="mdi:plus" />}
    >
      Neue Veranstaltung
    </Button>
  </Card>
);
